/**
 * AI 분석자료 (AI_PROTOCOL §3·§5·§15, AI_PROMPTS §12).
 * Layer A 현재 사례 상태 + Layer B 이번 회기 + Layer C 관련 이력. 과거 RAW 전사 전체는 보내지 않는다.
 * 실명·연락처·주소·생년월일·별칭은 절대 포함하지 않는다 (내담자 코드만).
 */
import type {
  CaseFormulationRevision,
  ClinicalEvidence,
  Client,
  CounselingGoal,
  DiagnosticConsideration,
  IntakeSummary,
  NextSessionBrief,
  RiskAssessment,
  Session,
  SessionAnalysis,
  Transcript,
  TranscriptSegment,
} from '../schema/v1';
import { RISK_KEYS } from '../schema/v1';
import { RISK_LABELS } from '../workflow/brief';
import { goalStatusLabel } from '../workflow/documents';
import { FORMULATION_ITEM_FIELDS, FORMULATION_LABELS, FORMULATION_LIST_FIELDS } from '../workflow/formulation';
import {
  APPROACH_BACKFILL_TASK,
  PROMPT_VERSIONS,
  SECOND_REVIEW_TASK,
  SESSION_OUTPUT_SPEC,
  SESSION_TASK,
} from './prompts';
import { PROFILE_FIELDS, type ProfileField } from '../workflow/profileMerge';
import { approachListText, treatmentPacketText, type ApproachScore } from '../workflow/approaches';

const mmss = (ms: number | undefined) => {
  const s = Math.floor((ms ?? 0) / 1000);
  const h = Math.floor(s / 3600);
  const p = (n: number) => String(n).padStart(2, '0');
  return h ? `${h}:${p(Math.floor((s % 3600) / 60))}:${p(s % 60)}` : `${p(Math.floor(s / 60))}:${p(s % 60)}`;
};

/** 화자 표시: 신뢰도 높음 = 상담자/내담자, 0.6~0.85 = [추정], 그 외 = [불명] */
export function speakerTag(s: Pick<TranscriptSegment, 'clinicalRole' | 'roleConfidence' | 'roleSource'>): string {
  const sure = s.roleSource === 'user' || s.roleConfidence >= 0.85;
  if (s.clinicalRole === 'counselor') return sure ? '상담자' : '상담자[추정]';
  if (s.clinicalRole === 'client') return sure ? '내담자' : '내담자[추정]';
  return '[불명]';
}

/** 짧은 화자 표시: 상/내 = 확실, 상?/내? = 추정, ? = 불명 */
export function shortTag(s: Pick<TranscriptSegment, 'clinicalRole' | 'roleConfidence' | 'roleSource'>): string {
  const sure = s.roleSource === 'user' || s.roleConfidence >= 0.85;
  if (s.clinicalRole === 'counselor') return sure ? '상' : '상?';
  if (s.clinicalRole === 'client') return sure ? '내' : '내?';
  return '?';
}

/** 발언(턴) 번호: g12-0, g12-1 → g12 */
export const turnKey = (segmentId: string) => segmentId.replace(/-\d+$/, '');

export const TRANSCRIPT_LEGEND =
  '전사 형식: [번호 시각 화자] 발언. 화자: 상·내 = 확실, 상?·내? = 자동 판정 추정, ? = 판정 불가. (!확인) = 두 기기 전사가 달라 아직 확인 안 됨, (짧은 동의) = 짧은 맞장구성 동의. 맞장구는 생략. 근거를 적을 때는 [ ] 안의 번호(예: g12)를 그대로 쓴다.';

/**
 * 전사 줄 (짧은 형식): 한 사람이 한 번에 말한 문장들은 한 줄로 합치고, 앞 꼬리표는 짧게.
 * 전사 내용은 줄이지 않는다. 한 발언 안에서 화자가 갈리면 문장 단위 번호(g12-1)를 그대로 쓴다.
 */
export function transcriptLines(segments: TranscriptSegment[]): string[] {
  const segs = segments.filter((s) => !s.isBackchannel);
  const unitsPerTurn = new Map<string, number>();
  for (const s of segs) unitsPerTurn.set(turnKey(s.segmentId), (unitsPerTurn.get(turnKey(s.segmentId)) ?? 0) + 1);
  const groups: TranscriptSegment[][] = [];
  for (const s of segs) {
    const last = groups.at(-1);
    const prev = last?.at(-1);
    if (prev && turnKey(prev.segmentId) === turnKey(s.segmentId) && shortTag(prev) === shortTag(s)) last!.push(s);
    else groups.push([s]);
  }
  // 한 발언이 여러 줄로 나뉘었으면 그 발언은 문장 번호로 표시
  const linesPerTurn = new Map<string, number>();
  for (const g of groups)
    linesPerTurn.set(turnKey(g[0]!.segmentId), (linesPerTurn.get(turnKey(g[0]!.segmentId)) ?? 0) + 1);
  const out: string[] = [];
  for (const g of groups) {
    const key = turnKey(g[0]!.segmentId);
    if ((linesPerTurn.get(key) ?? 1) > 1) {
      for (const s of g) out.push(line(s.segmentId, [s]));
    } else out.push(line(key, g));
  }
  return out;
}

function line(id: string, g: TranscriptSegment[]): string {
  const flags = [
    g.some((s) => s.verificationStatus === 'needs-review') ? '(!확인)' : null,
    g.some((s) => s.weakAgreement) ? '(짧은 동의)' : null,
  ].filter(Boolean);
  return `[${id} ${mmss(g[0]!.startMs)} ${shortTag(g[0]!)}] ${g.map((s) => s.text.trim()).join(' ')}${flags.length ? ` ${flags.join(' ')}` : ''}`;
}

export type SessionPacketInput = {
  client: Client;
  intake?: IntakeSummary;
  session: Session;
  goals: CounselingGoal[];
  formulation?: CaseFormulationRevision;
  diagnosis?: DiagnosticConsideration;
  latestRisk?: RiskAssessment;
  previousBrief?: NextSessionBrief;
  recent: { sessionNumber: number; sessionDate: string; summary: string }[];
  priorClientReports: ClinicalEvidence[];
  working?: Transcript;
  memoText?: string;
  /** 앱의 축어록 1차 후보 구간 (단어·말투 기반, 참고용) */
  screenWindows?: { startMs: number; endMs: number; reasons: string[] }[];
  /** A33: 지금까지 쌓인 접근별 근거 (높은 순) */
  approachScores?: ApproachScore[];
  /** A35: 심리검사 점수 요약과 해석 요약 */
  assessments?: {
    testName: string;
    administeredAt?: string;
    summary?: string;
    interpretation?: string;
    approved: boolean;
  }[];
};

const list = (xs: (string | undefined)[] | undefined) =>
  (xs ?? [])
    .filter(Boolean)
    .map((x) => `- ${x}`)
    .join('\n') || '- (없음)';

export function buildSessionPacket(i: SessionPacketInput): string {
  const { client, session } = i;
  const out: string[] = [];
  out.push(`=== ${PROMPT_VERSIONS.sessionPacket} ===`);
  out.push(
    `[메타] 출력 형식: ${PROMPT_VERSIONS.sessionAnalysis} · 내담자: ${client.clientCode} · 회기: ${session.sessionNumber} · 날짜: ${session.sessionDate} · 시간: ${session.durationMinutes ?? '-'}분 · 형태: 개인상담`,
  );

  out.push('', '[A. 현재 사례 상태]');
  out.push(`주호소:\n${list(i.intake?.presentingProblems)}`);
  if (i.intake?.referralReason) out.push(`상담 신청 사유: ${i.intake.referralReason}`);
  const goals = i.goals.filter((g) => !g.deletedAt);
  out.push(
    `상담목표 (goalId | 목표 | 상태 | 합의):\n${goals.map((g) => `- ${g.id} | ${g.title} | ${goalStatusLabel(g.status)} | ${g.agreedWithClient ? '내담자와 합의' : '상담자의 임상적 목표'}`).join('\n') || '- (없음)'}`,
  );
  const f = i.formulation;
  if (f) {
    out.push(`현재 사례개념화 (v${f.version}):`);
    for (const k of FORMULATION_LIST_FIELDS) if (f[k]?.length) out.push(`${FORMULATION_LABELS[k]}:\n${list(f[k])}`);
    for (const k of FORMULATION_ITEM_FIELDS)
      if (f[k]?.length)
        out.push(
          `${FORMULATION_LABELS[k]}:\n${f[k]!.map((x) => `- (${x.status === 'hypothesis' ? '가설' : x.status === 'observation' ? '관찰' : '확인됨'}) ${x.text}`).join('\n')}`,
        );
  } else out.push('현재 사례개념화: (아직 없음)');
  if (i.diagnosis) {
    out.push(
      `진단적 고려 (v${i.diagnosis.version}, 공식 진단 아님):\n${i.diagnosis.considerations.map((c) => `- ${c.diagnosisName}: ${c.status}`).join('\n') || '- (없음)'}`,
    );
  }
  if (i.latestRisk) {
    const flagged = RISK_KEYS.filter((k) => i.latestRisk![k].status !== 'not_assessed').map(
      (k) =>
        `- ${RISK_LABELS[k]}: ${i.latestRisk![k].status}${i.latestRisk![k].followUpRequired ? ' (추적 필요)' : ''}`,
    );
    out.push(`위험 상태 (최근 기록):\n${flagged.join('\n') || '- 평가된 항목 없음'}`);
  }
  out.push(`보호요인:\n${list([...(i.intake?.strengths ?? []), ...(f?.protectiveFactors ?? [])])}`);
  if (i.assessments?.length)
    out.push(
      `심리검사 (해석은 검사 해석이지 내담자 사실이 아님):\n${i.assessments
        .map(
          (a) =>
            `- ${a.testName}${a.administeredAt ? ` (${a.administeredAt})` : ''}: ${a.summary ?? ''}${a.interpretation ? `\n  해석 요약${a.approved ? '' : '(상담자 확인 전)'}: ${a.interpretation}` : ''}`,
        )
        .join('\n')}`,
    );
  // 이미 기록된 배경 정보 (AI가 같은 내용을 다시 제안하지 않도록). 실명·연락처·생년·별칭은 여기에도 없다.
  const known = (Object.keys(PROFILE_FIELDS) as ProfileField[])
    .filter((k) => k !== 'presentingProblems' && k !== 'strengths')
    .map((k) => {
      const src = (PROFILE_FIELDS[k].target === 'client' ? client : (i.intake ?? {})) as Record<string, unknown>;
      const v = src[k];
      const text = Array.isArray(v) ? v.join(', ') : typeof v === 'string' ? v.replace(/\n+/g, ' / ') : '';
      return text ? `- ${PROFILE_FIELDS[k].label}: ${text}` : null;
    })
    .filter(Boolean);
  out.push(`[현재 내담자 정보]\n${known.join('\n') || '- (아직 없음)'}`);
  if (i.previousBrief) {
    out.push(
      `지난 회기에 세운 이번 회기 계획:\n${list(i.previousBrief.sessionObjectives)}\n확인하기로 한 것:\n${list(i.previousBrief.assessmentQuestions?.map((q) => q.question))}`,
    );
  }

  out.push(
    `[상담자가 이끌어 가고자 하는 방향]\n${client.counselingDirection?.trim() || '(적지 않음 — 사례개념화와 목표를 기준으로 판단)'}`,
  );
  out.push(
    `[치료 방향]\n${treatmentPacketText(client.treatmentPlan, i.approachScores ?? [])}\n접근 목록 (approachEvidence의 approach에 id를 쓴다):\n${approachListText()}`,
  );

  out.push('', '[B. 이번 회기]');
  out.push(`상담자 메모 (COUNSELOR_OBSERVATION):\n${i.memoText?.trim() || '(없음)'}`);
  if (i.working?.segments.length) {
    out.push(TRANSCRIPT_LEGEND, ...transcriptLines(i.working.segments));
  } else out.push('전사: (없음)');
  if (i.screenWindows?.length)
    out.push(
      `[앱 1차 후보 구간 — 단어·말투 기반 참고용, 맥락으로 다시 판단할 것]\n${i.screenWindows.map((w) => `- ${mmss(w.startMs)}~${mmss(w.endMs)}: ${w.reasons.slice(0, 3).join(', ')}`).join('\n')}`,
    );

  out.push('', '[C. 관련 이력]');
  out.push(
    `최근 회기 요약:\n${i.recent.map((r) => `- S${r.sessionNumber} (${r.sessionDate}): ${r.summary}`).join('\n') || '- (없음)'}`,
  );
  if (i.priorClientReports.length) {
    out.push(
      `이전에 확인된 내담자 진술:\n${i.priorClientReports.map((e) => `- (S${e.sourceSession ?? '?'}) ${e.text}`).join('\n')}`,
    );
  }
  out.push(`=== END ${PROMPT_VERSIONS.sessionPacket} ===`, '', SESSION_TASK, '', SESSION_OUTPUT_SPEC);
  return out.join('\n');
}

export type SecondReviewInput = {
  client: Client;
  session: Session;
  analysis: SessionAnalysis;
  formulation?: CaseFormulationRevision;
  goals: CounselingGoal[];
  evidence: ClinicalEvidence[];
  recommended: string[];
  counselorQuestions?: string;
};

/** Claude 2차 검토 (AI_PROTOCOL §15): 전체 전사를 다시 보내지 않는다 */
export function buildSecondReviewPacket(i: SecondReviewInput): string {
  const out: string[] = [`=== ${PROMPT_VERSIONS.secondReviewPacket} ===`];
  out.push(`[메타] 내담자: ${i.client.clientCode} · 회기: ${i.session.sessionNumber}`);
  out.push(
    '',
    '[Primary 분석]',
    `회기 핵심: ${i.analysis.sessionSummary ?? '(없음)'}`,
    `주요 사건:\n${list(i.analysis.keyEvents)}`,
  );
  for (const [label, items] of [
    ['정서', i.analysis.emotions],
    ['인지', i.analysis.cognitions],
    ['행동', i.analysis.behaviors],
    ['관계', i.analysis.relationshipPatterns],
  ] as const)
    if (items?.length) out.push(`${label}:\n${items.map((x) => `- (${x.status}) ${x.text}`).join('\n')}`);
  out.push(
    `상담자 개입:\n${list(i.analysis.interventions?.map((x) => `${x.interventionName}${x.clientResponse ? ` → 반응: ${x.clientResponse}` : ''}`))}`,
  );
  if (i.formulation) {
    out.push(`[현재 사례개념화 v${i.formulation.version}]`);
    for (const k of FORMULATION_ITEM_FIELDS)
      if (i.formulation[k]?.length)
        out.push(`${FORMULATION_LABELS[k]}:\n${list(i.formulation[k]!.map((x) => x.text))}`);
  }
  const byType = (t: ClinicalEvidence['evidenceType']) =>
    i.evidence
      .filter((e) => e.evidenceType === t && !e.deletedAt)
      .map((e) => `${e.text}${e.needsVerification ? ' (확인 필요)' : ''}`);
  out.push(
    `[근거]\n내담자 진술:\n${list(byType('CLIENT_REPORT'))}\n상담자 관찰:\n${list(byType('COUNSELOR_OBSERVATION'))}\n가설:\n${list(byType('HYPOTHESIS'))}`,
  );
  out.push(
    `[상담목표]\n${list(i.goals.filter((g) => !g.deletedAt).map((g) => `${g.title} (${goalStatusLabel(g.status)})`))}`,
  );
  out.push(`[추천된 개입]\n${list(i.recommended)}`);
  out.push(`[상담자의 질문]\n${i.counselorQuestions?.trim() || '(없음)'}`);
  out.push(`=== END ${PROMPT_VERSIONS.secondReviewPacket} ===`, '', SECOND_REVIEW_TASK);
  return out.join('\n');
}

export type ApproachBackfillInput = {
  client: Client;
  presentingProblems?: string[];
  formulation?: CaseFormulationRevision;
  approachScores?: ApproachScore[];
  sessions: { sessionNumber: number; sessionDate: string; summary?: string; segments: TranscriptSegment[] }[];
};

/** 이전 회기 근거 한꺼번에 채우기 (A33): 회기마다 전사 전체 (내담자 코드만) */
export function buildApproachBackfillPacket(i: ApproachBackfillInput): string {
  const out: string[] = [`=== ${PROMPT_VERSIONS.approachBackfillPacket} ===`];
  out.push(
    `[메타] 출력 형식: ${PROMPT_VERSIONS.approachEvidence} · 내담자: ${i.client.clientCode} · 회기: ${i.sessions.map((s) => s.sessionNumber).join(', ')}`,
  );
  out.push(`주호소:\n${list(i.presentingProblems)}`);
  if (i.formulation) {
    const hyps = FORMULATION_ITEM_FIELDS.flatMap((k) => (i.formulation![k] ?? []).map((x) => x.text));
    if (hyps.length) out.push(`현재 사례개념화 (v${i.formulation.version}):\n${list(hyps.slice(0, 12))}`);
  }
  out.push(
    `[치료 방향]\n${treatmentPacketText(i.client.treatmentPlan, i.approachScores ?? [])}\n접근 목록 (approach에 id를 쓴다):\n${approachListText()}`,
  );
  out.push(TRANSCRIPT_LEGEND);
  for (const s of i.sessions) {
    out.push('', `[S${s.sessionNumber} · ${s.sessionDate}]`);
    if (s.summary) out.push(`요약: ${s.summary}`);
    out.push(...transcriptLines(s.segments));
  }
  out.push(`=== END ${PROMPT_VERSIONS.approachBackfillPacket} ===`, '', APPROACH_BACKFILL_TASK);
  return out.join('\n');
}
